<template>
  <div
    ref="slider"
    :class="{
      'range-slider': true,
      'tooltip-top': tooltip === 'top',
      'tooltip-bottom': tooltip === 'bottom',
    }"
  >
    <div class="handle-container">
      <div
        class="slider-touch"
        :style="{
          left: `${leftPercentage * 100}%`,
        }"
        @mousedown.stop.prevent="onStart($event, leftHandleIndex)"
        @touchstart.stop.prevent="onStart($event, leftHandleIndex)"
      >
        <span ref="left-handle">
          <span>{{ prepend }}{{ values[0] }}</span>
        </span>
      </div>
      <div
        class="slider-touch"
        :style="{
          left: `${rightPercentage * 100}%`,
        }"
        @mousedown.stop.prevent="onStart($event, rightHandleIndex)"
        @touchstart.stop.prevent="onStart($event, rightHandleIndex)"
      >
        <span ref="right-handle">
          <span>{{ prepend }}{{ values[1] }}</span>
        </span>
      </div>
    </div>
    <div class="slider-line">
      <span
        :style="{ marginLeft: `${lineX * 100}%`, width: `${lineWidth * 100}%` }"
      ></span>
    </div>
  </div>
</template>

<script>
/**
 * Range input with two input handles
 *
 * Based on https://codepen.io/zebresel/pen/xGLYOM
 */

export default {
  props: {
    input: {
      type: Array,
      default() {
        return [0, 100];
      },
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    /**
     * Minimum value to increase decrease
     */
    step: {
      type: Number,
      default: 1,
    },
    /**
     * Prepend the value of the tooltip with some value
     */
    prepend: {
      type: String,
      default: '',
    },
    /**
     * Position of the tooltip, top or bottom
     */
    tooltip: {
      type: String,
      default: 'top',
    },
    /**
     * Makes the slider return values exponentially,
     * values higher than 1 will make the lower end
     * of the slider/values more important/take more space
     */
    pow: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      values: [...this.input],
      minValue: this.min,
      maxValue: this.max,
      hasReadDomValues: false,
      currentHandleIndex: -1,
      leftHandleIndex: 0,
      rightHandleIndex: 1,
      leftHandleWidth: 0,
      rightHandleWidth: 0,
      maxX: 0,
    };
  },
  computed: {
    lineX() {
      return this.leftPercentage;
    },
    lineWidth() {
      return this.rightPercentage - this.leftPercentage;
    },
    leftPercentage() {
      if (this.minValue === this.maxValue) {
        return 0;
      }
      if (this.pow !== -1) {
        return Math.pow(
          (this.values[0] - this.minValue) / (this.maxValue - this.minValue),
          1 / this.pow
        );
      } else {
        return (
          (this.values[0] - this.minValue) / (this.maxValue - this.minValue)
        );
      }
    },
    rightPercentage() {
      if (this.minValue === this.maxValue) {
        return 1;
      }
      if (this.pow !== -1) {
        return Math.pow(
          (this.values[1] - this.minValue) / (this.maxValue - this.minValue),
          1 / this.pow
        );
      } else {
        return (
          (this.values[1] - this.minValue) / (this.maxValue - this.minValue)
        );
      }
    },
    leftX() {
      return this.leftPercentage * this.maxX;
    },
    rightX() {
      return this.rightPercentage * this.maxX;
    },
  },
  watch: {
    input(input) {
      if (input[0] !== this.values[0] || input[1] !== this.values[1]) {
        this.values = input;
      }
    },
    min(value) {
      if (value !== this.minValue) {
        if (this.values[0] < value || this.values[0] === this.minValue) {
          this.$set(this.values, 0, value);
        }
        if (this.values[1] < value) {
          this.$set(this.values, 1, this.maxValue);
        }
        this.minValue = value;
      }
    },
    max(value) {
      if (this.values[0] > value) {
        this.$set(this.values, 0, this.minValue);
      }
      if (this.values[1] > value || this.values[1] === this.maxValue) {
        this.$set(this.values, 1, value);
      }
      this.maxValue = value;
    },
  },
  methods: {
    readDomValues() {
      this.hasReadDomValues = true;

      this.leftHandleWidth = this.$refs['left-handle'].offsetWidth;
      this.rightHandleWidth = this.$refs['right-handle'].offsetWidth;

      // Define max amount the right handle can move on the x-axis
      const sliderWidth = this.$refs['slider'].offsetWidth;
      this.maxX = sliderWidth - this.rightHandleWidth;

      // Conver the step in value space to pixel space
      this.stepX = (this.step / (this.max - this.min)) * this.maxX;
    },
    onStart(event, handleIndex) {
      // Only on the first UI interaction we read the DOM values,
      // it could be that up till now the UI was hidden and the
      // values would be wrong or all 0
      if (!this.hasReadDomValues) {
        this.readDomValues();
      }

      let eventTouch = event;
      if (event.touches) {
        eventTouch = event.touches[0];
      }

      this.currentHandleIndex = handleIndex;
      if (this.currentHandleIndex === this.leftHandleIndex) {
        this.startX = eventTouch.pageX - this.leftX;
      } else if (this.currentHandleIndex === this.rightHandleIndex) {
        this.startX = eventTouch.pageX - this.rightX;
      }

      document.addEventListener('mousemove', this.onMove);
      document.addEventListener('mouseup', this.onStop);
      document.addEventListener('touchmove', this.onMove);
      document.addEventListener('touchend', this.onStop);
    },
    onMove(event) {
      if (this.currentHandleIndex === -1) {
        return;
      }

      var eventTouch = event;
      if (event.touches) {
        eventTouch = event.touches[0];
      }

      let x = eventTouch.pageX - this.startX;

      if (this.currentHandleIndex === this.leftHandleIndex) {
        // Left
        if (x > this.rightX - this.leftHandleWidth) {
          x = this.rightX - this.leftHandleWidth;
        } else if (x < 0) {
          x = 0;
        }
      } else if (this.currentHandleIndex === this.rightHandleIndex) {
        // Right
        if (x < this.leftX + this.leftHandleWidth) {
          x = this.leftX + this.leftHandleWidth;
        } else if (x > this.maxX) {
          x = this.maxX;
        }
      }

      let value;
      if (this.pow !== -1) {
        value = this.min + Math.pow(x / this.maxX, this.pow) * (this.max - this.min);
      } else {
        value = this.min + (x / this.maxX) * (this.max - this.min);
      }
      if (this.step !== 0) {
        this.$set(
          this.values,
          this.currentHandleIndex,
          Math.round(value / this.step) * this.step
        );
      } else {
        this.$set(this.values, this.currentHandleIndex, value);
      }

      this.$emit('update', this.values);
    },
    onStop() {
      document.removeEventListener('mousemove', this.onMove);
      document.removeEventListener('mouseup', this.onStop);
      document.removeEventListener('touchmove', this.onMove);
      document.removeEventListener('touchend', this.onStop);

      this.currentHandleIndex = -1;

      this.$emit('update', this.values);
      this.$emit('drag-end', this.values);
    },
  },
};
</script>
